/**
 * News & Advice featured videos: open YouTube videos in a lightbox and track viewing in GA4.
 *
 * The YouTube player (and its cookies) only loads once a visitor clicks a video.
 */

const VIDEO_MODULE_NAME = 'news_advice_featured_videos';
const PROGRESS_MILESTONES = [ 25, 50, 75 ];

let youTubeApiPromise = null;

// Same rule as spotlightPushDataLayer() in script.js: only send analytics from production.
function pushVideoEvent( event, video, player, extra ) {
  if ( ! window.spotlightIsProduction ) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push( {
    event,
    videoProvider: 'youtube',
    videoModule: VIDEO_MODULE_NAME,
    videoTitle: video.title,
    videoUrl: 'https://www.youtube.com/watch?v=' + video.id,
    videoDuration: Math.round( player.getDuration() || 0 ),
    videoCurrentTime: Math.round( player.getCurrentTime() || 0 ),
    ...extra,
  } );
}

function loadYouTubeApi() {
  if ( window.YT && window.YT.Player ) {
    return Promise.resolve( window.YT );
  }

  if ( youTubeApiPromise ) {
    return youTubeApiPromise;
  }

  youTubeApiPromise = new Promise( function ( resolve, reject ) {
    const previousCallback = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = function () {
      if ( typeof previousCallback === 'function' ) {
        previousCallback();
      }

      resolve( window.YT );
    };

    const script = document.createElement( 'script' );
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = function () {
      youTubeApiPromise = null;
      reject( new Error( 'YouTube IFrame API failed to load' ) );
    };
    document.head.appendChild( script );
  } );

  return youTubeApiPromise;
}

function initNewsVideos() {
  const section = document.getElementById( 'news-videos' );
  const dialog = section?.querySelector( '.news-video-dialog' );

  if ( ! dialog || typeof dialog.showModal !== 'function' ) {
    // No <dialog> support: cards fall back to opening YouTube.
    return;
  }

  const playerContainer = dialog.querySelector( '.news-video-dialog__player' );
  let player = null;
  let progressTimer = null;
  let session = null;

  const stopProgressTimer = function () {
    window.clearInterval( progressTimer );
    progressTimer = null;
  };

  const checkProgress = function () {
    const duration = player.getDuration();

    if ( ! duration ) {
      return;
    }

    const percent = ( player.getCurrentTime() / duration ) * 100;

    PROGRESS_MILESTONES.forEach( function ( milestone ) {
      if ( percent >= milestone && ! session.milestones.has( milestone ) ) {
        session.milestones.add( milestone );
        pushVideoEvent( 'video_progress', session.video, player, { videoPercent: milestone } );
      }
    } );
  };

  const onStateChange = function ( event ) {
    const states = window.YT.PlayerState;

    if ( event.data === states.PLAYING ) {
      if ( ! session.started ) {
        session.started = true;
        pushVideoEvent( 'video_start', session.video, player, { videoPercent: 0 } );
      }

      if ( ! progressTimer ) {
        progressTimer = window.setInterval( checkProgress, 1000 );
      }
    } else {
      stopProgressTimer();
    }

    if ( event.data === states.ENDED && ! session.completed ) {
      session.completed = true;
      pushVideoEvent( 'video_complete', session.video, player, { videoPercent: 100 } );
    }
  };

  // Used when the IFrame API is blocked (e.g. by an ad blocker): plays the video, without tracking.
  const embedPlainIframe = function ( video ) {
    const iframe = document.createElement( 'iframe' );
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent( video.id ) + '?autoplay=1&rel=0&playsinline=1';
    iframe.title = video.title;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.addEventListener( 'load', hideLoading );
    playerContainer.replaceChildren( iframe, createLoading( video ) );
  };

  // The YouTube player takes a few seconds to boot, so show the video's thumbnail and a spinner meanwhile.
  const createLoading = function ( video ) {
    const loading = document.createElement( 'div' );
    loading.className = 'news-video-dialog__loading';
    loading.setAttribute( 'aria-hidden', 'true' );

    if ( video.thumbnail ) {
      const img = document.createElement( 'img' );
      img.src = video.thumbnail;
      img.alt = '';
      loading.appendChild( img );
    }

    const spinner = document.createElement( 'span' );
    spinner.className = 'news-video-dialog__spinner';
    loading.appendChild( spinner );

    return loading;
  };

  const hideLoading = function () {
    playerContainer.querySelector( '.news-video-dialog__loading' )?.remove();
  };

  const openVideo = function ( video, trigger ) {
    session = { video, trigger, started: false, completed: false, milestones: new Set() };
    dialog.setAttribute( 'aria-label', video.title );
    dialog.showModal();

    const mount = document.createElement( 'div' );
    playerContainer.replaceChildren( mount, createLoading( video ) );

    loadYouTubeApi()
      .then( function ( YT ) {
        // Dialog closed (or another video opened) while the API was loading.
        if ( ! dialog.open || session?.video !== video ) {
          return;
        }

        player = new YT.Player( mount, {
          host: 'https://www.youtube-nocookie.com',
          videoId: video.id,
          width: '100%',
          height: '100%',
          playerVars: { autoplay: 1, rel: 0, playsinline: 1 },
          events: {
            onReady( event ) {
              event.target.getIframe().title = video.title;
              hideLoading();
            },
            onStateChange,
          },
        } );
      } )
      .catch( function () {
        if ( dialog.open && session?.video === video ) {
          embedPlainIframe( video );
        }
      } );
  };

  // Runs for the close button, Esc and backdrop clicks.
  dialog.addEventListener( 'close', function () {
    stopProgressTimer();

    if ( player ) {
      player.destroy();
      player = null;
    }

    playerContainer.replaceChildren();

    // Return focus to the card that opened the video (Safari doesn't focus links on click).
    session?.trigger?.focus( { preventScroll: true } );
    session = null;
  } );

  const closeButton = dialog.querySelector( '.news-video-dialog__close' );

  closeButton?.addEventListener( 'click', function () {
    dialog.close();
  } );

  // Loop keyboard focus inside the dialog. Browsers otherwise let Tab leave a modal <dialog> for the
  // browser UI, and tabbing out of the (cross-origin) YouTube iframe continues past the dialog.
  // Tab past the player lands on the end guard -> close button; Shift+Tab from the close button lands
  // on the start guard -> back into the player.
  // Safety net: if focus lands anywhere on the page outside the open dialog (e.g. a third-party
  // widget that isn't made inert), bring it back to the close button.
  document.addEventListener( 'focusin', function ( event ) {
    if ( dialog.open && ! dialog.contains( event.target ) ) {
      closeButton?.focus();
    }
  } );

  dialog.querySelectorAll( '[data-focus-guard]' ).forEach( function ( guard ) {
    guard.addEventListener( 'focus', function () {
      const iframe = playerContainer.querySelector( 'iframe' );
      const target = guard.dataset.focusGuard === 'start' && iframe ? iframe : closeButton;

      target?.focus();
    } );
  } );

  // Clicking the dimmed backdrop (the dialog element itself, outside the inner box) closes it.
  dialog.addEventListener( 'click', function ( event ) {
    if ( event.target === dialog ) {
      dialog.close();
    }
  } );

  // Warm up the connections to YouTube once the visitor shows interest, so the player starts faster on click.
  // Preconnecting only opens the connection; nothing is requested (and no cookies are set) until a video is opened.
  const preconnect = function () {
    [ 'https://www.youtube-nocookie.com', 'https://www.youtube.com' ].forEach( function ( origin ) {
      const link = document.createElement( 'link' );
      link.rel = 'preconnect';
      link.href = origin;
      document.head.appendChild( link );
    } );
  };

  section.addEventListener( 'pointerover', preconnect, { once: true } );
  section.addEventListener( 'focusin', preconnect, { once: true } );

  section.addEventListener( 'click', function ( event ) {
    const card = event.target.closest( '.news-video-card' );

    // Let modified clicks (new tab/window) go to YouTube as normal.
    if ( ! card || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0 ) {
      return;
    }

    event.preventDefault();
    openVideo( { id: card.dataset.videoId, title: card.dataset.videoTitle, thumbnail: card.querySelector( 'img' )?.src }, card );
  } );
}

if ( document.readyState === 'loading' ) {
  document.addEventListener( 'DOMContentLoaded', initNewsVideos );
} else {
  initNewsVideos();
}
